/**
 * Mock Image SVG Renderer
 *
 * Web component for rendering artwork composited into SVG templates.
 * Supports multiple artwork placements and responsive srcset.
 * SVG can be provided via URL (svg_file reference) or inline markup.
 */

class MockImageSvg extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = null;
    this.isInitialized = false;
    this.imageIds = [];
    this.idSuffix = '';
    this.boundHandleArtworkChanged = this.handleArtworkChanged.bind(this);
  }

  /**
   * Rewrites every id in the parsed SVG and every reference to those ids so
   * the instance is fully self-contained. Without this, multiple copies of the
   * same SVG on one page (e.g. main slide + every gallery thumbnail) share ids
   * and Chrome resolves `url(#id)` / `xlink:href="#id"` against the first
   * occurrence in the document, so thumbnails end up pointing at the main
   * slide's <image> — which doesn't paint until that slide becomes visible.
   */
  static _namespaceSvgIds(svgElement, suffix) {
    const REF_ATTRS = [
      'fill', 'stroke', 'clip-path', 'mask', 'filter',
      'marker-start', 'marker-mid', 'marker-end',
      'cursor', 'fill-rule',
    ];
    const urlRefRe = /url\(#([^)\s]+)\)/g;

    svgElement.querySelectorAll('[id]').forEach(el => {
      el.setAttribute('id', el.getAttribute('id') + suffix);
    });

    const walk = (el) => {
      const hrefAttrs = ['href', 'xlink:href'];
      for (const name of hrefAttrs) {
        const val = el.getAttribute(name);
        if (val && val.charAt(0) === '#') {
          el.setAttribute(name, val + suffix);
        }
      }
      for (const name of REF_ATTRS) {
        const val = el.getAttribute(name);
        if (val && val.indexOf('url(#') !== -1) {
          el.setAttribute(name, val.replace(urlRefRe, (_, id) => `url(#${id}${suffix})`));
        }
      }
      const style = el.getAttribute('style');
      if (style && style.indexOf('url(#') !== -1) {
        el.setAttribute('style', style.replace(urlRefRe, (_, id) => `url(#${id}${suffix})`));
      }
    };
    walk(svgElement);
    svgElement.querySelectorAll('*').forEach(walk);
  }

  /**
   * Picks the smallest srcset candidate that covers the element's rendered
   * width at the current device pixel ratio, falling back to the largest
   * candidate (e.g. when the element isn't laid out yet and measures 0).
   * Returns null when the srcset is empty or unparseable.
   */
  static _pickFromSrcset(srcset, element) {
    if (!srcset) return null;
    const candidates = srcset
      .split(',')
      .map(entry => {
        const parts = entry.trim().split(/\s+/);
        const width = parseInt(parts[1], 10);
        return parts[0] && width > 0 ? { url: parts[0], width } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.width - b.width);
    if (candidates.length === 0) return null;

    const targetWidth = (element.clientWidth || 0) * (window.devicePixelRatio || 1);
    const match = candidates.find(c => c.width >= targetWidth);
    return (match || candidates[candidates.length - 1]).url;
  }

  async connectedCallback() {
    // Listen for artwork changes from parent container
    const container = this.closest('.mock-image-container');
    if (container) {
      container.addEventListener('artwork-changed', this.boundHandleArtworkChanged);
    }
    try {
      // Show loading state while fetching
      this.style.backgroundColor = 'var(--color-background, #f5f5f5)';

      // Get SVG source - prefer URL (file reference), fallback to inline markup
      const svgUrl = this.dataset.svgUrl;
      let svgMarkup = this.dataset.svgMarkup;

      // Fetch SVG from URL if provided
      if (svgUrl) {
        try {
          const response = await fetch(svgUrl);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          svgMarkup = await response.text();
        } catch (fetchError) {
          console.error('[MockImageSvg] Failed to fetch SVG from URL:', svgUrl, fetchError);
          this.renderError(`Failed to load SVG: ${fetchError.message}`);
          return;
        }
      }

      if (!svgMarkup) {
        console.error('[MockImageSvg] No SVG markup provided');
        this.renderError('No SVG provided via data-svg-url or data-svg-markup attribute');
        return;
      }

      // Get image IDs to replace (comma-separated) - store for later updates
      this.imageIds = (this.dataset.svgImageIds || '').split(',').map(id => id.trim()).filter(Boolean);
      if (this.imageIds.length === 0) {
        console.error('[MockImageSvg] No image IDs provided', {
          svgSource: svgUrl || 'inline',
          dataset: this.dataset
        });
        this.renderError('No image IDs provided in data-svg-image-ids attribute');
        return;
      }

      // Get artwork URLs
      const artworkSrc = this.dataset.artworkSrc;
      const artworkSrcset = this.dataset.artworkSrcset;
      if (!artworkSrc) {
        console.error('[MockImageSvg] No artwork source provided');
        this.renderError('No artwork source provided in data-artwork-src attribute');
        return;
      }

      // Pick a right-sized candidate from the srcset rather than always using
      // data-artwork-src (the full-crop original). The candidates mirror the
      // CSS-mode <img> srcset URLs, so the pick usually hits the browser cache
      // instead of downloading the artwork again at a unique size.
      this.artworkUrl = MockImageSvg._pickFromSrcset(artworkSrcset, this) || artworkSrc;

      console.log('[MockImageSvg] Initializing with:', {
        imageIds: this.imageIds,
        artworkSrc,
        artworkUrl: this.artworkUrl,
        svgSource: svgUrl ? 'url' : 'inline',
        svgLength: svgMarkup.length
      });

      // Parse SVG markup
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgMarkup, 'image/svg+xml');

      // Check for parsing errors
      const parserError = svgDoc.querySelector('parsererror');
      if (parserError) {
        console.error('[MockImageSvg] Failed to parse SVG:', parserError.textContent);
        this.renderError(`SVG parsing error: ${parserError.textContent}`);
        return;
      }

      const svgElement = svgDoc.documentElement;
      if (!svgElement || svgElement.tagName !== 'svg') {
        console.error('[MockImageSvg] Invalid SVG markup', { tagName: svgElement?.tagName });
        this.renderError('Invalid SVG markup - expected <svg> root element');
        return;
      }

      // Namespace every id + reference so multiple copies of this SVG on the
      // page don't share ids (Chrome resolves `url(#id)` globally).
      MockImageSvg._instanceCounter = (MockImageSvg._instanceCounter || 0) + 1;
      this.idSuffix = `__mis${MockImageSvg._instanceCounter}`;
      MockImageSvg._namespaceSvgIds(svgElement, this.idSuffix);

      // Set image hrefs BEFORE appending to the live DOM.
      let replacedCount = 0;
      this.imageIds.forEach(imageId => {
        const scopedId = imageId + this.idSuffix;
        const imageElement = svgElement.querySelector(`#${CSS.escape(scopedId)}`);
        if (imageElement) {
          imageElement.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', this.artworkUrl);
          imageElement.setAttribute('href', this.artworkUrl);
          replacedCount++;
          console.log(`[MockImageSvg] Replaced image #${imageId}`);
        } else {
          console.warn(`[MockImageSvg] Image element with id "${imageId}" not found in SVG`);
        }
      });

      // Append SVG to the live DOM
      this.innerHTML = '';
      this.style.backgroundColor = 'transparent';
      this.appendChild(svgElement);

      // Chrome heavily deprioritizes fetches for SVG <image> elements that
      // only render through <defs>/<pattern>/<use>, so the artwork often
      // doesn't load until an unrelated paint kicks networking. Prime the
      // browser's HTTP cache here so the pattern can paint on first layout.
      if (this.artworkUrl) {
        const preload = new Image();
        preload.decoding = 'async';
        preload.src = this.artworkUrl;
      }

      // Re-set hrefs after the SVG is live to kick browsers that skip the
      // initial image load for off-viewport elements.
      requestAnimationFrame(() => {
        this.imageIds.forEach(imageId => {
          const img = svgElement.querySelector(`#${CSS.escape(imageId + this.idSuffix)}`);
          if (img) img.setAttribute('href', this.artworkUrl);
        });
      });


      if (replacedCount === 0) {
        console.warn('[MockImageSvg] No image elements were replaced. Check that svg_image_ids match actual IDs in the SVG.');
      }

      // Add class for styling
      svgElement.classList.add('mock-image__svg');

      // Ensure SVG scales responsively
      if (!svgElement.hasAttribute('preserveAspectRatio')) {
        svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      }

      // Set up resize observer for responsive behavior (if needed)
      const shouldDisableObserver = this.hasAttribute('data-disable-resize-observer') ||
                                     this.dataset.disableResizeObserver === 'true';

      if (!shouldDisableObserver) {
        // Observe container for size changes
        const container = this.closest('.mock-image-container') || this.parentElement;

        this.resizeObserver = new ResizeObserver(() => {
          // Currently no action needed on resize - SVG scales naturally
          // This is here for future enhancements (e.g., quality switching)
        });
        this.resizeObserver.observe(container);
      }

      this.isInitialized = true;
      console.log('[MockImageSvg] Successfully initialized');
    } catch (error) {
      console.error('[MockImageSvg] Error initializing:', error);
      this.renderError(`Initialization error: ${error.message}`);
    }
  }

  renderError(message) {
    this.innerHTML = `
      <div style="padding: 20px; background: #fee; border: 2px solid #f00; color: #c00; font-family: monospace; font-size: 12px;">
        <strong>MockImageSvg Error:</strong><br>
        ${message}
      </div>
    `;
  }

  /**
   * Handles artwork-changed event from parent container
   */
  handleArtworkChanged(event) {
    const container = this.closest('.mock-image-container');
    if (!container) return;

    // Deliberately NOT re-picking from data-artwork-srcset here: dispatchers
    // (photo-upload flows in media-gallery.js / photo-print-media-gallery.js)
    // only update data-artwork-src with the new artwork URL — the srcset still
    // points at the original artwork and would render the wrong image.
    const newArtworkSrc = container.dataset.artworkSrc;
    if (newArtworkSrc) {
      this.updateArtwork(newArtworkSrc);
    }
  }

  /**
   * Updates the artwork URL in the already-rendered SVG
   * @param {string} newArtworkSrc - The new artwork URL to use
   */
  updateArtwork(newArtworkSrc) {
    if (!this.isInitialized || !newArtworkSrc) return;

    const svgElement = this.querySelector('svg');
    if (!svgElement) return;

    const preload = new Image();
    preload.decoding = 'async';
    preload.src = newArtworkSrc;

    this.imageIds.forEach(imageId => {
      const imageElement = svgElement.querySelector(`#${CSS.escape(imageId + this.idSuffix)}`);
      if (imageElement) {
        imageElement.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', newArtworkSrc);
        imageElement.setAttribute('href', newArtworkSrc);
      }
    });
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Clean up event listener
    const container = this.closest('.mock-image-container');
    if (container) {
      container.removeEventListener('artwork-changed', this.boundHandleArtworkChanged);
    }
  }
}

// Register the custom element
if (!customElements.get('mock-image-svg')) {
  customElements.define('mock-image-svg', MockImageSvg);
}
